import MuiDialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import clsx from 'clsx';
import Button from 'core/modules/atoms/Button/Button';
import Hr from 'core/modules/atoms/Hr/Hr';
import HeaderTabs from 'core/modules/components/tabs/HeaderTabs/HeaderTabs';
import FooterButtons from 'core/modules/compositions/FooterButtons';
import React from 'react';
import Icon from 'ui/atoms/Icon/Icon';
import useDialog from './useDialog';

export type DialogSize = 'xs' | 's' | 'm' | 'l' | 'xl' | 'xxl' | 'all-screen';

type DialogTab = Readonly<{
  id?: string | number;
  text?: string;
  title?: string;
  [key: string]: unknown;
}>;

type DialogProps = Readonly<{
  ariaLabel?: string;
  buttonTextCancel?: string;
  buttonTextSuccess?: string;
  children?: React.ReactNode;
  className?: string;
  customFooterLeft?: React.ReactNode;
  customHeader?: React.ReactNode;
  hasFooterBackground?: boolean;
  hasSeparator?: boolean;
  iconTitle?: string;
  id?: string;
  isAlert?: boolean;
  isCloseButtonHidden?: boolean;
  isDisabledSuccess?: boolean;
  isHeaderHidden?: boolean;
  isLoading?: boolean;
  isOpen?: boolean;
  onCancel?: () => void;
  // eslint-disable-next-line no-unused-vars
  onChangeTab?: (nextTabIndex: number) => void;
  onClose?: () => void;
  onSuccess?: () => void;
  size?: DialogSize;
  tabActive?: number;
  tabs?: ReadonlyArray<DialogTab>;
  text?: string;
  title?: React.ReactNode;
}>;

function Dialog({
  ariaLabel,
  buttonTextCancel,
  buttonTextSuccess,
  children,
  className,
  customFooterLeft,
  customHeader,
  hasFooterBackground = false,
  hasSeparator = false,
  iconTitle,
  id,
  isAlert = false,
  isCloseButtonHidden = false,
  isDisabledSuccess = false,
  isHeaderHidden = false,
  isLoading = false,
  isOpen = false,
  onCancel,
  onChangeTab,
  onClose,
  onSuccess,
  size = 'm',
  tabActive,
  tabs,
  text,
  title,
}: DialogProps): React.JSX.Element {
  const { closeLabel, descriptionId, resolvedButtonTextCancel, resolvedButtonTextSuccess, titleId } = useDialog({
    buttonTextCancel,
    buttonTextSuccess,
    id,
  });

  const hasTitle = Boolean(title);
  const shouldShowCloseButton = Boolean(onClose) && !isCloseButtonHidden && !isHeaderHidden;
  const hasHeader = !isHeaderHidden && (hasTitle || shouldShowCloseButton || Boolean(customHeader));
  const hasFooter = Boolean(onSuccess) || Boolean(onCancel);
  const hasDescription = Boolean(text);

  return (
    <MuiDialog
      className={clsx('dialog', className, `dialog--size-${size}`, {
        'dialog--with-footer-with-background': hasFooterBackground,
        'dialog--with-icon': iconTitle,
      })}
      fullWidth
      maxWidth={false}
      onClose={onClose}
      open={isOpen}
      PaperProps={{
        role: isAlert ? 'alertdialog' : 'dialog',
        'aria-labelledby': hasTitle ? titleId : undefined,
        'aria-label': hasTitle ? undefined : ariaLabel,
        'aria-describedby': hasDescription ? descriptionId : undefined,
        'aria-modal': true,
      }}
    >
      {hasHeader && (
        <div className="dialog__header">
          <div className="dialog__title-container">
            {hasTitle && (
              <div className="dialog__title-wrapper">
                {iconTitle && <Icon name={iconTitle} color="primary" />}
                <h2 className="dialog__title" id={titleId}>{title}</h2>
              </div>
            )}
            {tabs && (
              <div className="dialog__title-tabs">
                <HeaderTabs onChangeTab={onChangeTab} tabActive={tabActive} tabs={tabs} />
              </div>
            )}
            <div className="dialog__close-container">
              {shouldShowCloseButton && (
                <div className="dialog__close">
                  <Button accessibilityOnly ariaLabel={closeLabel} onClick={onClose} text="">
                    <Icon name="close" size="xl" />
                  </Button>
                </div>
              )}
            </div>
          </div>
          {hasDescription && <span className="dialog__text" id={descriptionId}>{text}</span>}
          {customHeader && <div className="dialog__custom-header">{customHeader}</div>}
          {hasSeparator && (
            <div className="dialog__hr">
              <Hr size="small" />
            </div>
          )}
        </div>
      )}
      {children && <DialogContent>{children}</DialogContent>}
      {hasFooter && (
        <DialogActions>
          <div className="dialog__actions">
            <div className="dialog__actions-left">{customFooterLeft}</div>
            <FooterButtons
              autoFocusCancel={isAlert && Boolean(onCancel)}
              buttonTextCancel={resolvedButtonTextCancel}
              buttonTextSucess={resolvedButtonTextSuccess}
              disabledSuccess={isDisabledSuccess}
              loading={isLoading}
              onCancel={onCancel}
              onSuccess={onSuccess}
            />
          </div>
        </DialogActions>
      )}
    </MuiDialog>
  );
}

export default Dialog;
